import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { Device, TelemetryMetric } from '../types';
import { LIVE_FIELDS, PANEL_TYPES, type LiveField, type Panel, type PanelType } from './types';
import {
  EMPTY_DRAFT,
  draftFromPanel,
  draftToPanel,
  validateDraft,
  type PanelDraft,
} from './editor-state';
import { ChartPanel } from './panels/ChartPanel';
import { StatPanel } from './panels/StatPanel';
import { GaugePanel } from './panels/GaugePanel';
import { FaultsPanel } from './panels/FaultsPanel';
import { LivePanel } from './panels/LivePanel';

export interface PanelEditorProps {
  open: boolean;
  initial: Panel | null; // null = add mode
  metrics: TelemetryMetric[];
  devices: Device[];
  timeRange: string;
  onApply: (draft: PanelDraft) => void;
  onClose: () => void;
}

const TYPE_LABEL: Record<PanelType, string> = {
  chart: 'Chart',
  stat: 'Stat',
  gauge: 'Gauge',
  faults: 'Faults',
  live: 'Live',
};

const FIELD_LABEL: Record<LiveField, string> = {
  joint_temperatures: 'Joint temperatures',
  joint_positions: 'Joint positions',
  cycle_time_ms: 'Cycle time',
  pick_success_rate: 'Pick success rate',
  power_watts: 'Power',
  cpu_temp: 'CPU temperature',
};

const SECTION_LABEL = 'text-xs font-medium text-gray-500 uppercase tracking-wide';

export function PanelEditor({
  open,
  initial,
  metrics,
  devices,
  timeRange,
  onApply,
  onClose,
}: PanelEditorProps) {
  const [draft, setDraft] = useState<PanelDraft>(initial ? draftFromPanel(initial) : EMPTY_DRAFT);

  // Reinitialize whenever the drawer opens (or targets a different panel).
  useEffect(() => {
    setDraft(initial ? draftFromPanel(initial) : EMPTY_DRAFT);
  }, [initial, open]);

  // Escape closes the drawer (mirrors the app's ConfirmDialog convention).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const promMetrics = metrics.filter((m) => m.source === 'prom');
  const selectedMetric = metrics.find((m) => m.id === draft.metric);
  const selectedUnit = selectedMetric?.unit;
  const error = validateDraft(draft, metrics);

  const usesMetric = draft.type === 'chart' || draft.type === 'stat' || draft.type === 'gauge';
  const usesSerialList = usesMetric || draft.type === 'faults';

  const toggleSerial = (serial: string) =>
    setDraft((d) => ({
      ...d,
      serials: d.serials.includes(serial)
        ? d.serials.filter((s) => s !== serial)
        : [...d.serials, serial],
    }));

  const previewPanel = error === null
    ? draftToPanel(draft, 'preview', { x: 0, y: 0, w: 6, h: 4 }, metrics)
    : null;

  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-label="Panel editor"
      className="fixed right-0 top-0 bottom-0 w-96 bg-surface-1 border-l border-surface-4 z-50 overflow-y-auto p-5 space-y-5"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-white">Panel editor</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Type picker */}
      <fieldset className="space-y-2">
        <legend className={SECTION_LABEL}>Type</legend>
        <div className="grid grid-cols-3 gap-2">
          {PANEL_TYPES.map((type) => (
            <label
              key={type}
              className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-sm cursor-pointer transition-colors ${
                draft.type === type
                  ? 'border-kinetica-600 bg-kinetica-600/10 text-white'
                  : 'border-surface-4 text-gray-300 hover:border-surface-3'
              }`}
            >
              <input
                type="radio"
                name="panel-type"
                value={type}
                checked={draft.type === type}
                onChange={() => setDraft((d) => ({ ...d, type }))}
                className="accent-kinetica-600"
              />
              {TYPE_LABEL[type]}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Metric picker — chart/stat/gauge only */}
      {usesMetric && (
        <div className="space-y-1.5">
          <label className={SECTION_LABEL} htmlFor="panel-metric">
            Metric
          </label>
          <select
            id="panel-metric"
            aria-label="Metric"
            className="input w-full"
            value={draft.metric ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, metric: e.target.value || null }))}
          >
            <option value="">Select a metric…</option>
            {promMetrics.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Gauge bounds */}
      {draft.type === 'gauge' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className={SECTION_LABEL} htmlFor="gauge-min">
              Min
            </label>
            <input
              id="gauge-min"
              type="number"
              className="input w-full"
              value={draft.min ?? ''}
              onChange={(e) =>
                setDraft((d) => ({ ...d, min: e.target.value === '' ? null : Number(e.target.value) }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <label className={SECTION_LABEL} htmlFor="gauge-max">
              Max
            </label>
            <input
              id="gauge-max"
              type="number"
              className="input w-full"
              value={draft.max ?? ''}
              onChange={(e) =>
                setDraft((d) => ({ ...d, max: e.target.value === '' ? null : Number(e.target.value) }))
              }
            />
          </div>
        </div>
      )}

      {/* Device selection — serial checklist for chart/stat/gauge/faults */}
      {usesSerialList && (
        <fieldset className="space-y-2">
          <legend className={SECTION_LABEL}>Devices</legend>
          <p className="text-xs text-gray-500">
            Leave empty to include all of your devices (resolved by the server).
          </p>
          <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border border-surface-4 p-2">
            {devices.length === 0 && <p className="text-xs text-gray-500">No devices available.</p>}
            {devices.map((device) => (
              <label
                key={device.serial}
                className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="accent-kinetica-600"
                  checked={draft.serials.includes(device.serial)}
                  onChange={() => toggleSerial(device.serial)}
                />
                <span className="font-mono text-xs text-gray-400">{device.serial}</span>
                <span className="truncate">{device.name}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {/* Live: single device + field */}
      {draft.type === 'live' && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className={SECTION_LABEL} htmlFor="live-device">
              Device
            </label>
            <select
              id="live-device"
              aria-label="Device"
              className="input w-full"
              value={draft.serial ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, serial: e.target.value || null }))}
            >
              <option value="">Select a device…</option>
              {devices.map((device) => (
                <option key={device.serial} value={device.serial}>
                  {device.serial} — {device.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={SECTION_LABEL} htmlFor="live-field">
              Field
            </label>
            <select
              id="live-field"
              aria-label="Field"
              className="input w-full"
              value={draft.field ?? ''}
              onChange={(e) =>
                setDraft((d) => ({ ...d, field: (e.target.value || null) as LiveField | null }))
              }
            >
              <option value="">Select a field…</option>
              {LIVE_FIELDS.map((field) => (
                <option key={field} value={field}>
                  {FIELD_LABEL[field]}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Stat reduce */}
      {draft.type === 'stat' && (
        <div className="space-y-1.5">
          <label className={SECTION_LABEL} htmlFor="stat-reduce">
            Reduce
          </label>
          <select
            id="stat-reduce"
            className="input w-full"
            value={draft.reduce}
            onChange={(e) =>
              setDraft((d) => ({ ...d, reduce: e.target.value as PanelDraft['reduce'] }))
            }
          >
            <option value="last">Last</option>
            <option value="avg">Average</option>
            <option value="max">Max</option>
          </select>
        </div>
      )}

      {/* Title */}
      <div className="space-y-1.5">
        <label className={SECTION_LABEL} htmlFor="panel-title">
          Title
        </label>
        <input
          id="panel-title"
          type="text"
          className="input w-full"
          placeholder={selectedMetric?.label ?? 'Panel title'}
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
        />
      </div>

      {/* Live preview */}
      {previewPanel && (
        <div className="space-y-1.5">
          <span className={SECTION_LABEL}>Preview</span>
          <div className="h-48 rounded-lg border border-surface-4 bg-surface-2 overflow-hidden">
            {previewPanel.type === 'chart' && (
              <ChartPanel panel={previewPanel} timeRange={timeRange} refreshMs={0} />
            )}
            {previewPanel.type === 'stat' && (
              <StatPanel panel={previewPanel} timeRange={timeRange} refreshMs={0} unit={selectedUnit} />
            )}
            {previewPanel.type === 'gauge' && (
              <GaugePanel panel={previewPanel} timeRange={timeRange} refreshMs={0} unit={selectedUnit} />
            )}
            {previewPanel.type === 'faults' && (
              <FaultsPanel panel={previewPanel} timeRange={timeRange} refreshMs={0} />
            )}
            {previewPanel.type === 'live' && (
              <LivePanel panel={previewPanel} telemetry={new Map()} isConnected={false} />
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="space-y-3 pt-2">
        {error && <p className="text-sm text-amber-400">{error}</p>}
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onApply(draft)}
            disabled={error !== null}
            className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </aside>
  );
}
